import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Chip,
    Divider,
    CircularProgress,
    IconButton,
    Paper,
    Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EmailIcon from "@mui/icons-material/Email";
import TaskService from "../../services/Task.service";
import PreviewEmailTaskDialog from "./PreviewEmailTaskDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

function formatDue(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isOverdue(dateStr) {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr + "T00:00:00") < today;
}

function PreviewRow({ label, value }) {
    if (!value) return null;
    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                {value}
            </Typography>
        </Box>
    );
}

export default function TaskPreviewDialog({ task, onClose, currentUserId, onTaskUpdated, onEdit, onDeleted }) {
    const [completing, setCompleting] = useState(false);
    const [localSubtasks, setLocalSubtasks] = useState([]);
    const [togglingSubtask, setTogglingSubtask] = useState(null);
    const [emailOpen, setEmailOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isAssignee = task?.assignee_detail?.id === currentUserId;
    const isCreatedBy = task?.created_by_detail?.id === currentUserId;
    const canEdit = isAssignee || isCreatedBy;

    useEffect(() => {
        setLocalSubtasks(task?.subtasks_detail || []);
        setConfirmDeleteOpen(false);
    }, [task]);

    if (!task) return null;

    const due = formatDue(task.due_date);
    const overdue = !task.is_complete && isOverdue(task.due_date);
    const project = task.project_detail;
    const quote = task.quote_detail;

    const handleToggleComplete = () => {
        setCompleting(true);
        const next = !task.is_complete;
        TaskService.update(task.id, {
            is_complete: next,
            assignee: task.assignee_detail?.id,
            tasklist: task.tasklist_detail?.id,
        })
            .then((res) => onTaskUpdated(res.data))
            .catch(() => {})
            .finally(() => setCompleting(false));
    };

    const handleDelete = () => {
        setDeleting(true);
        TaskService.remove(task.id)
            .then(() => {
                onDeleted(task);
                onClose();
            })
            .catch(() => {})
            .finally(() => setDeleting(false));
    };

    const handleToggleSubtask = (sub) => {
        if (togglingSubtask) return;
        setTogglingSubtask(sub.id);
        const next = !sub.is_complete;
        TaskService.updateSubTask(sub.id, { is_complete: next })
            .then(() => {
                setLocalSubtasks((prev) =>
                    prev.map((s) => s.id === sub.id ? { ...s, is_complete: next } : s)
                );
            })
            .catch(() => {})
            .finally(() => setTogglingSubtask(null));
    };

    return (
        <>
            <Dialog
                open={Boolean(task)}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        borderTop: 4,
                        borderColor: task.is_public ? "primary.main" : "success.main",
                    },
                }}
            >
                <DialogTitle sx={{ pr: 1, pb: 0.5 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                                lineHeight: 1.3,
                                textDecoration: task.is_complete ? "line-through" : "none",
                                color: task.is_complete ? "text.disabled" : "text.primary",
                                flex: 1,
                                minWidth: 0,
                                pt: 0.5,
                            }}
                        >
                            {task.title}
                        </Typography>
                        <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                            {canEdit && (
                                <IconButton size="small" onClick={() => onEdit(task)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                            {canEdit && (
                                <IconButton size="small" color="error" onClick={() => setConfirmDeleteOpen(true)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {/* Badges */}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                        <Chip
                            label={task.is_public ? "Public" : "Private"}
                            size="small"
                            color={task.is_public ? "primary" : "success"}
                            icon={task.is_public ? undefined : <LockIcon />}
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.65rem" }}
                        />
                        {task.is_complete && (
                            <Chip
                                label="Completed"
                                size="small"
                                color="default"
                                icon={<CheckCircleIcon />}
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.65rem" }}
                            />
                        )}
                        {due && (
                            <Chip
                                label={due}
                                size="small"
                                color={overdue ? "error" : "default"}
                                variant={overdue ? "filled" : "outlined"}
                                sx={{ fontFamily: "Montserrat, sans-serif", fontSize: "0.65rem" }}
                            />
                        )}
                    </Stack>
                </DialogTitle>

                <Divider sx={{ mt: 1.5 }} />

                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Stack spacing={1.75}>
                        <PreviewRow label="Assignee" value={task.assignee_detail?.full_name} />
                        <PreviewRow label="Task List" value={task.tasklist_detail?.title} />
                        {project && (
                            <PreviewRow label="Project" value={`${project.number} – ${project.name}`} />
                        )}
                        {quote && (
                            <PreviewRow label="Quote" value={`${quote.number} – ${quote.name}`} />
                        )}
                        <PreviewRow label="Created By" value={task.created_by_detail?.full_name} />

                        {task.notes && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                                >
                                    Notes
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "Montserrat, sans-serif", mt: 0.25, whiteSpace: "pre-wrap" }}
                                >
                                    {task.notes}
                                </Typography>
                            </Box>
                        )}

                        {/* Subtasks — interactive for assignee */}
                        {localSubtasks.length > 0 && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.25 }}
                                >
                                    Sub-tasks{" "}
                                    <Typography component="span" variant="caption" color="text.disabled">
                                        {localSubtasks.filter((s) => s.is_complete).length}/{localSubtasks.length}
                                    </Typography>
                                </Typography>
                                <Stack spacing={0}>
                                    {localSubtasks.map((sub) => (
                                        <Stack
                                            key={sub.id}
                                            direction="row"
                                            alignItems="center"
                                            sx={{
                                                cursor: isAssignee ? "pointer" : "default",
                                                borderRadius: 1,
                                                px: 0.5,
                                                "&:hover": isAssignee ? { bgcolor: "action.hover" } : {},
                                            }}
                                            onClick={() => isAssignee && handleToggleSubtask(sub)}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={sub.is_complete}
                                                disabled={!isAssignee || togglingSubtask === sub.id}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => isAssignee && handleToggleSubtask(sub)}
                                                sx={{ p: 0.5 }}
                                                icon={<RadioButtonUncheckedIcon sx={{ fontSize: "0.95rem", color: "text.disabled" }} />}
                                                checkedIcon={<CheckCircleIcon sx={{ fontSize: "0.95rem", color: "success.main" }} />}
                                            />
                                            {togglingSubtask === sub.id ? (
                                                <CircularProgress size={12} sx={{ ml: 0.5 }} />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: "Montserrat, sans-serif",
                                                        textDecoration: sub.is_complete ? "line-through" : "none",
                                                        color: sub.is_complete ? "text.disabled" : "text.primary",
                                                        ml: 0.5,
                                                    }}
                                                >
                                                    {sub.title}
                                                </Typography>
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Linked email */}
                        {task.linked_email_detail && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.75 }}
                                >
                                    Linked Email
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    onClick={() => setEmailOpen(true)}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "action.hover" },
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                    }}
                                >
                                    <EmailIcon fontSize="small" color="action" sx={{ mt: 0.25, flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            noWrap
                                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                                        >
                                            {task.linked_email_detail.subject || "(No Subject)"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ fontFamily: "Montserrat, sans-serif", display: "block" }}
                                        >
                                            From: {task.linked_email_detail.from_email}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                {isAssignee && (
                    <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                        <Button
                            fullWidth
                            variant={task.is_complete ? "outlined" : "contained"}
                            color={task.is_complete ? "inherit" : "success"}
                            startIcon={
                                completing
                                    ? <CircularProgress size={16} />
                                    : task.is_complete
                                    ? <RadioButtonUncheckedIcon />
                                    : <CheckCircleIcon />
                            }
                            onClick={handleToggleComplete}
                            disabled={completing}
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {task.is_complete ? "Mark Incomplete" : "Mark Complete"}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <PreviewEmailTaskDialog
                open={emailOpen}
                onClose={() => setEmailOpen(false)}
                email={task.linked_email_detail}
            />

            <DeleteConfirmDialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={handleDelete}
                taskTitle={task.title}
                deleting={deleting}
            />
        </>
    );
}
